'use client';

import classNames from 'classnames';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: React.ReactNode | string;
  children: React.ReactNode;
  className?: string;
  tooltipClassName?: string;
  arrowColorClassName?: string;
  variant?: 'default' | 'widgetTitle';
  position?: TooltipPosition;
  bgColorClassName?: string;
  bgColor?: string;
  disablePortal?: boolean;
  portalElement?: HTMLElement | null;
  wrap?: boolean;
}

export const Tooltip = ({
  content,
  children,
  className,
  tooltipClassName,
  arrowColorClassName,
  variant = 'default',
  position = 'top',
  bgColorClassName = 'bg-gray-800',
  bgColor,
  disablePortal = false,
  portalElement,
  wrap = false,
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number; transform: string } | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const tooltipElRef = useRef<HTMLDivElement | null>(null);
  const hasClamped = useRef(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    hasClamped.current = false;
    const rect = el.getBoundingClientRect();
    const margin = 8; // space between trigger and tooltip

    if (variant === 'widgetTitle') {
      // Centered above the info icon, 4px gap from icon top
      const centerX = rect.left + rect.width / 2;
      const top = rect.top - 4;
      setTooltipPos({ top, left: centerX, transform: 'translate(-50%, -100%)' });
    } else {
      // Default: centered relative to the trigger, based on `position`
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      if (position === 'top') {
        const top = rect.top - margin;
        setTooltipPos({ top, left: centerX, transform: 'translate(-50%, -100%)' });
      } else if (position === 'bottom') {
        const top = rect.bottom + margin;
        setTooltipPos({ top, left: centerX, transform: 'translate(-50%, 0)' });
      } else if (position === 'left') {
        const left = rect.left - margin;
        setTooltipPos({ top: centerY, left, transform: 'translate(-100%, -50%)' });
      } else {
        const left = rect.right + margin;
        setTooltipPos({ top: centerY, left, transform: 'translate(0, -50%)' });
      }
    }
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
    updatePosition();
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Recalculate on scroll/resize while visible
  useEffect(() => {
    if (!isVisible) return;
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [isVisible, variant, position]);

  // Clamp tooltip to viewport after it renders
  useEffect(() => {
    if (!isVisible || !tooltipPos || hasClamped.current || !tooltipElRef.current) return;
    hasClamped.current = true;
    const el = tooltipElRef.current;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    const vw = window.innerWidth;
    let left = tooltipPos.left;
    if (rect.left < pad) left = tooltipPos.left + (pad - rect.left);
    else if (rect.right > vw - pad) left = tooltipPos.left - (rect.right - (vw - pad));
    if (left !== tooltipPos.left) setTooltipPos(prev => prev ? { ...prev, left } : prev);
  }, [isVisible, tooltipPos]);

  const tooltipContent =
    isVisible && tooltipPos ? (
      <div
        ref={tooltipElRef}
        className={classNames(
          'fixed text-sm text-white rounded-lg shadow-lg px-3 py-2 z-[1000]',
          !bgColor && bgColorClassName,
          variant === 'widgetTitle'
            ? 'w-[240px] max-w-[240px] whitespace-normal text-left'
            : wrap ? 'whitespace-normal' : 'whitespace-nowrap',
          tooltipClassName,
        )}
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          transform: tooltipPos.transform,
          ...(bgColor ? { backgroundColor: bgColor } : {}),
        }}
      >
        {content}
        <div
          className={classNames(
            'absolute w-0 h-0 border-transparent',
            // Center arrow under tooltip for widgetTitle
            variant === 'widgetTitle'
              ? 'top-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-t-4'
              : position === 'top'
                ? 'top-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-t-4'
                : position === 'bottom'
                  ? 'bottom-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-b-4'
                  : position === 'left'
                    ? 'left-full top-1/2 -translate-y-1/2 border-t-4 border-b-4 border-l-4'
                    : 'right-full top-1/2 -translate-y-1/2 border-t-4 border-b-4 border-r-4',
            arrowColorClassName,
            // Tooltip triangle color (matches bg). We support the default bg explicitly.
            // For custom bg classes, use Tailwind arbitrary values or extend config.
            bgColorClassName === 'bg-gray-800' && (variant === 'widgetTitle' || position === 'top') && 'border-t-gray-800',
            bgColorClassName === 'bg-gray-800' && position === 'bottom' && 'border-b-gray-800',
            bgColorClassName === 'bg-gray-800' && position === 'left' && 'border-l-gray-800',
            bgColorClassName === 'bg-gray-800' && position === 'right' && 'border-r-gray-800'
          )}
          style={
            bgColor
              ? {
                ...(variant === 'widgetTitle' || position === 'top' ? { borderTopColor: bgColor } : {}),
                ...(position === 'bottom' ? { borderBottomColor: bgColor } : {}),
                ...(position === 'left' ? { borderLeftColor: bgColor } : {}),
                ...(position === 'right' ? { borderRightColor: bgColor } : {}),
              }
              : undefined
          }
        />
      </div>
    ) : null;

  return (
    <>
      <div
        ref={triggerRef}
        className={classNames('inline-block', className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {mounted && tooltipContent && (
        disablePortal
          ? tooltipContent
          : createPortal(tooltipContent, portalElement ?? document.body)
      )}
    </>
  );
};
